// /onboarding/shape — shape 2, «كم ملعباً لديك؟» (board 25 v2 shape--2,
// board 22 §ج): the places (name, «نشط» / «متوقّف») and the owner's hourly
// price bands («سعر الساعة حسب الوقت»: from, to, price; one band is enough,
// bands are optional). The place noun is the category's (terminology R6).
// «إضافة ملعب» posts «ملعب {n+1}» at once (editable inline, saved on leave);
// the bands are a replace-set PUT once every row is complete. Finish needs one
// ACTIVE place (R8; the line W12 until then). Writes: booking direct.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { normalizeDigits, toArabicDigits } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { placeTerms } from '@/lib/terminology';
import { Msg } from '../../components/widgets';
import { Wizard } from '../../components/Wizard';
import { PLACES, SHAPE_COMMON, SHAPE_COPY } from '../../copy';
import { finishShape } from '../../data/enroll';
import { createPlace, getBands, listPlaces, patchPlace, putBands, type Place, type PriceBand } from '../../data/shapeApi';
import { bandSummary, hhmm, profileOf } from '../../preview';
import { toMinutes } from '../../state';
import { useBusinessPreview } from '../Business';
import { firstMessage, useShapeAction } from './useShapeAction';

interface BandDraft {
  from: string;
  to: string;
  price: string;
}

const draftOf = (b: PriceBand): BandDraft => ({ from: hhmm(b.from_min), to: hhmm(b.to_min), price: String(b.price_iqd) });

/** A complete band row → the wire band; null while any field is missing. */
function bandOf(d: BandDraft): PriceBand | null {
  const from = toMinutes(d.from);
  const to = toMinutes(d.to);
  const price = Number(normalizeDigits(d.price));
  if (from == null || to == null || !d.price.trim() || !Number.isFinite(price)) return null;
  return { from_min: from, to_min: to === 0 ? 1440 : to, price_iqd: price };
}

const sameBands = (a: PriceBand[], b: PriceBand[]) => JSON.stringify(a) === JSON.stringify(b);

export function PlacesStep({ businessId, categoryId }: { businessId: string; categoryId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const terms = placeTerms(categoryId);
  const placesKey = ['onboarding', 'places', businessId];
  const bandsKey = ['onboarding', 'bands', businessId];
  const places = useQuery({ queryKey: placesKey, queryFn: () => listPlaces(businessId), staleTime: Infinity, retry: 0 });
  const bands = useQuery({ queryKey: bandsKey, queryFn: () => getBands(businessId), staleTime: Infinity, retry: 0 });
  const { act, busy, error } = useShapeAction();
  const [names, setNames] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<BandDraft[] | null>(null);
  const [bandsError, setBandsError] = useState<string | null>(null);

  // The band rows start from the server's set (once).
  useEffect(() => {
    if (drafts == null && bands.data) setDrafts(bands.data.map(draftOf));
  }, [bands.data, drafts]);

  const list = places.data ?? [];
  const saved = bands.data ?? [];
  const { profile, preview } = useBusinessPreview('shape');
  const hasActive = list.some((p) => p.active);

  const setPlaces = (fn: (xs: Place[]) => Place[]) => qc.setQueryData<Place[]>(placesKey, (xs) => fn(xs ?? []));

  const add = () =>
    act(async () => {
      const name = `${terms.placeSing} ${toArabicDigits((qc.getQueryData<Place[]>(placesKey) ?? []).length + 1)}`;
      const p = await createPlace(businessId, { name });
      setPlaces((xs) => [...xs, p]);
    });

  const toggle = (p: Place) =>
    act(
      async () => {
        const next = await patchPlace(businessId, p.id, { active: !p.active });
        setPlaces((xs) => xs.map((x) => (x.id === p.id ? next : x)));
      },
      (e) => setRowErrors((r) => ({ ...r, [p.id]: firstMessage(e) })),
    );

  const rename = (p: Place) => {
    const v = (names[p.id] ?? p.name).trim();
    if (v === p.name) return;
    void act(
      async () => {
        const next = await patchPlace(businessId, p.id, { name: v });
        setPlaces((xs) => xs.map((x) => (x.id === p.id ? next : x)));
        setRowErrors((r) => ({ ...r, [p.id]: '' }));
      },
      (e) => setRowErrors((r) => ({ ...r, [p.id]: firstMessage(e) })),
    );
  };

  /** The bands PUT (replace-set). `completeOnly`: a removal is saved on its own — the incomplete drafts stay local. */
  const saveBands = (rows: BandDraft[], completeOnly = false) => {
    const wire = rows.map(bandOf);
    if (!completeOnly && wire.some((b) => b == null)) return;
    const next = wire.filter((b): b is PriceBand => b != null);
    if (sameBands(next, saved)) return;
    void act(
      async () => {
        const res = await putBands(businessId, next);
        qc.setQueryData(bandsKey, res);
        setBandsError(null);
      },
      (e) => setBandsError(firstMessage(e)),
    );
  };

  const editBand = (i: number, patch: Partial<BandDraft>) => {
    setBandsError(null);
    setDrafts((ds) => (ds ?? []).map((d, j) => (j === i ? { ...d, ...patch } : d)));
  };
  const removeBand = (i: number) => {
    const rows = (drafts ?? []).filter((_, j) => j !== i);
    setDrafts(rows);
    saveBands(rows, true);
  };

  const loading = places.isPending || bands.isPending;
  const loadError = places.isError ? places.error : bands.isError ? bands.error : null;
  const rows = drafts ?? [];

  return (
    <Wizard
      screen="shape"
      testId={loading ? 'onb-shape-loading' : 'onb-shape-places'}
      state={loading ? 'loading' : busy ? 'saving' : 'default'}
      list
      question={SHAPE_COPY.place.question(categoryId)}
      sub={SHAPE_COPY.place.sub(categoryId, profileOf(categoryId))}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={!hasActive}>
          {SHAPE_COMMON.finish}
        </PrimaryAction>
      }
      onSubmit={() => {
        if (hasActive) finishShape(navigate);
      }}
      error={
        loadError
          ? { kind: 'network', onRetry: () => void Promise.all([places.refetch(), bands.refetch()]) }
          : error
      }
      panel={{ phase: 'preview', profile, preview }}
    >
      {loading ? (
        <Skeleton height={120} />
      ) : (
        <>
          <div className="onb-listcard" data-testid="onb-places">
            {list.map((p) => (
              <div key={p.id} className="onb-row onb-entity-row" data-testid="onb-place-row" data-active={p.active ? 'true' : 'false'}>
                <input
                  className="onb-entity-name"
                  aria-label={SHAPE_COMMON.name}
                  value={names[p.id] ?? p.name}
                  maxLength={60}
                  onChange={(e) => setNames((n) => ({ ...n, [p.id]: e.target.value }))}
                  onBlur={() => rename(p)}
                />
                <span className="onb-pill" data-tone={p.active ? 'ok' : 'warn'} data-testid="onb-place-state">
                  {p.active ? PLACES.active : PLACES.paused}
                </span>
                <button type="button" role="switch" aria-checked={p.active} aria-label={PLACES.active} className="onb-day-toggle" onClick={() => void toggle(p)}>
                  <span className="onb-day-knob" aria-hidden="true" />
                </button>
                {rowErrors[p.id] && <Msg testId="onb-place-error">{rowErrors[p.id]}</Msg>}
              </div>
            ))}
            <button type="button" className="onb-entity-add" onClick={() => void add()} disabled={busy}>
              <Plus aria-hidden="true" />
              {PLACES.add(terms.placeSing)}
            </button>
          </div>
          {!hasActive && (
            <Msg tone="info" testId="onb-shape-min-one">
              {SHAPE_COMMON.atLeastOne}
            </Msg>
          )}

          <div className="onb-entity-section" data-testid="onb-bands">
            <div className="onb-entity-section-title">{PLACES.bandsTitle}</div>
            <div className="onb-listcard">
              {rows.map((d, i) => {
                const complete = bandOf(d);
                // the summary is the server's band equal to this row (not its index: the server sorts)
                const savedBand = complete ? saved.find((b) => sameBands([b], [complete])) : undefined;
                return (
                  <div key={i} className="onb-row onb-band-row">
                    <input type="time" className="onb-time ltr" dir="ltr" step={900} aria-label="من" data-testid={`onb-band-from-${i}`} value={d.from} onChange={(e) => editBand(i, { from: e.target.value })} onBlur={() => saveBands(rows)} />
                    <span className="onb-day-dash" aria-hidden="true">
                      –
                    </span>
                    <input type="time" className="onb-time ltr" dir="ltr" step={900} aria-label="إلى" data-testid={`onb-band-to-${i}`} value={d.to} onChange={(e) => editBand(i, { to: e.target.value })} onBlur={() => saveBands(rows)} />
                    <input
                      className="onb-entity-price ltr"
                      dir="ltr"
                      inputMode="numeric"
                      aria-label={SHAPE_COMMON.price}
                      data-testid={`onb-band-price-${i}`}
                      value={d.price}
                      onChange={(e) => editBand(i, { price: normalizeDigits(e.target.value) })}
                      onBlur={() => saveBands(rows)}
                    />
                    <button type="button" className="onb-entity-remove" aria-label="حذف" onClick={() => removeBand(i)}>
                      <X aria-hidden="true" />
                    </button>
                    {savedBand && (
                      <span className="onb-row-sub onb-band-summary" data-testid={`onb-band-summary-${i}`}>
                        {bandSummary(savedBand)}
                      </span>
                    )}
                  </div>
                );
              })}
              <button type="button" className="onb-entity-add" onClick={() => setDrafts([...rows, { from: '', to: '', price: '' }])}>
                <Plus aria-hidden="true" />
                {PLACES.addBand}
              </button>
            </div>
            {bandsError && <Msg testId="onb-bands-error">{bandsError}</Msg>}
          </div>
        </>
      )}
    </Wizard>
  );
}
